// ScrollArea — a quiet scroll container primitive, pure.
//
// A thin wrapper over @radix-ui/react-scroll-area: Radix owns the hard
// parts (cross-browser custom scrollbars, pointer/keyboard scrolling,
// RTL, resize observation); this component only styles them to the
// foundations register and picks which scrollbars to show.
//
// Register (.claude/research/design/06): the scrollbars are QUIET — hidden at rest,
// revealed on hover-or-scroll (Radix `type="hover"`), a neutral ink-tint
// thumb that deepens on interaction (the same translucent-fill discipline
// as Button, §2). No accent — a scrollbar is chrome, never the primary
// action or the agent's voice. For chat logs, lists, and editor regions.
//
// Purely presentational and props-driven: it has no intrinsic user-facing
// text (so no i18n), reads no domain state, and reports nothing up. Deps —
// the content — arrive as `children`.
//
// `orientation` is a discriminated variant, not three boolean flags: it
// names the intent ("this region scrolls vertically") and selects the
// scrollbar set structurally, so illegal combinations can't be expressed.

import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import type { ReactNode, Ref } from "react";
import "./ScrollArea.css";

export type ScrollAreaOrientation = "vertical" | "horizontal" | "both";

export type ScrollAreaProps = {
    readonly children: ReactNode;
    // Caps the scroll region's height; beyond it the content scrolls.
    // A bare number is treated as px (React CSSProperties semantics).
    readonly maxHeight?: string | number;
    readonly orientation?: ScrollAreaOrientation;
    // Handle on the scrolling viewport element, for callers that must
    // manage scroll position (e.g. a chat transcript pinning its tail).
    readonly viewportRef?: Ref<HTMLDivElement>;
    readonly className?: string;
};

export const ScrollArea = ({
    children,
    maxHeight,
    orientation = "vertical",
    viewportRef,
    className,
}: ScrollAreaProps) => {
    const showVertical = orientation === "vertical" || orientation === "both";
    const showHorizontal =
        orientation === "horizontal" || orientation === "both";

    return (
        <ScrollAreaPrimitive.Root
            type="hover"
            className={`bilda-scroll-area${className ? ` ${className}` : ""}`}
            style={maxHeight === undefined ? undefined : { maxHeight }}
        >
            {/* tabIndex: a scroll region whose content holds no focusable
             * element is unreachable — and unscrollable — by keyboard (axe
             * scrollable-region-focusable, WCAG 2.1.1). Focusing the viewport
             * itself makes arrow/page keys scroll it; the CSS pairs this with
             * a :focus-visible ring so the stop is visible, and mouse users
             * never see it. */}
            <ScrollAreaPrimitive.Viewport
                ref={viewportRef}
                className="bilda-scroll-area__viewport"
                tabIndex={0}
            >
                {children}
            </ScrollAreaPrimitive.Viewport>

            {showVertical && (
                <ScrollAreaPrimitive.Scrollbar
                    orientation="vertical"
                    className="bilda-scroll-area__bar"
                >
                    <ScrollAreaPrimitive.Thumb className="bilda-scroll-area__thumb" />
                </ScrollAreaPrimitive.Scrollbar>
            )}

            {showHorizontal && (
                <ScrollAreaPrimitive.Scrollbar
                    orientation="horizontal"
                    className="bilda-scroll-area__bar"
                >
                    <ScrollAreaPrimitive.Thumb className="bilda-scroll-area__thumb" />
                </ScrollAreaPrimitive.Scrollbar>
            )}

            {orientation === "both" && (
                <ScrollAreaPrimitive.Corner className="bilda-scroll-area__corner" />
            )}
        </ScrollAreaPrimitive.Root>
    );
};
